import { findRow, formatPrice } from "@/data/pricing";
import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  absoluteUrl,
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/fractional-coo-sydney";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional COO Sydney | Ajility";
const DESCRIPTION =
  "Fractional COO support for founder-led Sydney businesses doing $3M to $30M. On site weekly across the CBD, Parramatta and Norwest.";

const faqs = [
  {
    q: "How often would you be on site in Sydney?",
    a: "Sydney is the base, so on site is the default. A retainer normally runs one day a week in the business, with the weekly leadership session in person. Businesses in the CBD, the inner west, Parramatta, Norwest, Macquarie Park and the south west are all within a normal commute, so the cadence does not depend on travel scheduling.",
  },
  {
    q: "Do you work with Sydney businesses outside the metropolitan area?",
    a: "Yes. Engagements run out to the Blue Mountains, the Hawkesbury, Wollongong and the Southern Highlands. Past about ninety minutes from the CBD the pattern usually shifts to a fortnightly on-site day with the alternate week run remotely. There are no travel charges within New South Wales; travel time is absorbed in the price.",
  },
  {
    q: "What does a fractional COO cost in Sydney?",
    a: "The same as anywhere else in Australia, because Ajility prices deliverables rather than time. The Operating Diagnostic is from $3,600 plus GST, the Full Diagnostic and 90-Day Roadmap is from $6,900 plus GST for one entity, and most composed retainers for businesses under $15M start from $3,900 per month plus GST. Sydney does not carry a metropolitan loading.",
  },
  {
    q: "Which Sydney industries do you work in most?",
    a: "Professional and technical services, workplace technology and fit-out, construction and trade contracting, wholesale and distribution, and freight and logistics through the western Sydney industrial corridor. The common factor is a founder-led business between $3M and $30M where growth has outpaced structure.",
  },
  ...pageFaqAdditions["/fractional-coo-sydney"],
];

export const Route = createFileRoute("/fractional-coo-sydney")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Fractional COO and GM Sydney | Ajility" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO and GM Sydney | Ajility" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO in Sydney for founder-led businesses" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("fractional-coo-sydney", "Fractional COO services in Sydney from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Sydney", path: PATH }])),
      jsonLd(
        webPageSchema({ image: "fractional-coo-sydney",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Sydney",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-sydney",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "City", name: "Sydney" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: SydneyPage,
});

function SydneyPage() {
  return (
    <LocationPage
      city="Sydney"
      region="NSW"
      heading="Fractional COO in Sydney"
      lead="Hands-on COO leadership for Sydney founders running $3M to $30M businesses, delivered on site rather than by report. Sydney is where Ajility is based, so the weekly rhythm happens in the room. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role."
      intro={[
        "Sydney is a services city with an industrial edge. The CBD and the north shore run on professional services, technology and finance. Parramatta and Norwest carry the fast-growing mid-market firms that outgrew a home office five years ago. Western Sydney, from Eastern Creek out to Marsden Park, is warehousing, freight, wholesale and construction. The result is a market where a $10M business can be a fourteen-person consultancy or a hundred-person contractor, and the operating problems are nothing alike.",
        "What is alike is the cost base. Sydney carries the highest wage and premises costs in the country, which means an operating fault is expensive faster here than anywhere else. A team that is twenty per cent under-utilised, a project that quietly slips a month, a pricing model that has not moved since before the last two rounds of wage increases: all of these show up in the Sydney P&L before the founder has finished explaining why revenue is up.",
        "Ajility works with Sydney founders on exactly that gap. Not a strategy document, and not a report with recommendations. An operating rhythm, clear ownership and numbers that arrive early enough to change a decision.",
      ]}
      industries={[
        {
          name: "Professional and technical services",
          note: "Consultancies, engineering firms, agencies and advisory practices where the product is billable time. The recurring fault is utilisation and scope: work is priced on an estimate, delivered on goodwill and written off at invoice. Installing job-level margin visibility and a scope-change discipline usually finds several points of margin that were already earned.",
        },
        {
          name: "Workplace technology and fit-out",
          note: "Sydney's return-to-office and refurbishment cycle keeps AV, network, furniture and fit-out businesses busy. They tend to be project businesses running on a sales culture, with delivery capacity managed in a spreadsheet. The fix is a resourced forward schedule and gate reviews between sale and install.",
        },
        {
          name: "Construction and trade contracting",
          note: "Sub-contractors and specialist trades across the metropolitan build market. Security of payment timeframes, retentions and variation claims decide whether a profitable job is a profitable year. Job costing that lands weekly rather than at practical completion is the single highest-value change available.",
        },
        {
          name: "Wholesale, distribution and freight",
          note: "The western Sydney corridor from Silverwater to Eastern Creek. Stock, fleet and labour are the three big levers, and each one is usually managed by a different person with a different number. A single weekly operating scorecard tends to resolve arguments that have run for years.",
        },
        {
          name: "Healthcare and allied services",
          note: "Clinician-founded groups adding sites across the metropolitan area. Clinical quality is rarely the constraint. The constraint is that the founder is still the practice manager, the recruiter and the roster, and site three exposes it.",
        },
      ]}
      sections={[
        {
          heading: "How Sydney engagements run",
          paragraphs: [
            "Sydney engagements are on-site engagements. Ajility is based here, so there is no travel to schedule and no fly-in week to protect. A composed retainer typically runs a day a week inside the business, with the weekly leadership session held in person and the rest of the week available by phone for the decisions that cannot wait until Tuesday.",
            "The in-person part matters more in Sydney than the geography suggests. A dispersed business with a CBD office, a Rydalmere warehouse and crews on client sites does not have a single room where the operating truth is visible. Being physically in the warehouse and in the leadership meeting in the same week is what surfaces the difference between what the report says and what is actually happening.",
            "Remote work still carries the middle of the engagement. Documentation, scorecard build, operating and margin analysis and one-to-one coaching of the leadership team run between sessions. For businesses out towards the Blue Mountains, the Central Coast boundary or Wollongong, the pattern shifts to a fortnightly full day on site with structured remote weeks either side. There are no travel charges within New South Wales; travel time is absorbed in the price.",
          ],
        },
        {
          heading: "What tends to be wrong in a $3M to $30M Sydney business",
          paragraphs: [
            "The first thing is almost always decision routing. Everything climbs back to the founder because there are no thresholds and no owners. In a high-cost city this is expensive twice, once in the founder's time and again in the wait time of the well-paid people standing still.",
            "The second is margin visibility. Sydney businesses tend to know their revenue to the dollar and their job margin to the vibe. Once a job, client or site level view exists, the pattern is nearly always the same: a small number of clients or jobs are carrying the business and another group is quietly funded by them.",
            `Ajility works on site in Sydney with founder-led businesses of $3M to $30M, installing annual plans, 90-day priorities and weekly tracking. Work usually starts with the Operating Diagnostic, ${answerPrice("Operating Diagnostic")}. The third is cadence. Sydney founders are heavily networked and heavily interrupted. Without a fixed weekly leadership session, a monthly commercial review and a hard quarterly reset, priorities set in January are unrecognisable by March. Installing that rhythm is the work described in the Ajility Operating System and Governance service.`,
            <>For the trades version of the operating seat, read <Link to="/insights/$slug" params={{ slug: "hire-a-general-manager-or-a-fractional-one" }} className="underline underline-offset-4">should I hire a general manager or a fractional one</Link>.</>,
          ],
        },
      ]}
      example={{
        label: "Anonymised Sydney example",
        title: "A workplace technology business that was winning and drowning",
        body: [
          "An inner west workplace technology and fit-out business, founder-led, just over $12M in revenue, had grown by winning more of the same work. Sales were strong. Delivery was a queue. Every install date was negotiated personally by the founder, and the schedule lived in his head and a shared spreadsheet that three people edited.",
          "The first ninety days installed three things: a resourced forward schedule with a single owner, a gate review between the signed order and the install booking, and a weekly leadership session where the schedule, the pipeline and the labour plan were read together rather than separately. Job margin was reported weekly against the quoted margin, which had never previously been compared.",
          "The founder's involvement in scheduling ended in the second month. The visible change was not dramatic, which is the point: fewer reschedules, fewer weekend recoveries, and a leadership team that could answer a delivery question without walking into his office.",
        ],
      }}
      relatedLinks={[
        {
          to: "/services/strategy-execution",
          label: "Strategy to Execution",
          note: "Quarterly priorities with named owners, for Sydney leadership teams that plan well and deliver unevenly.",
        },
        {
          to: "/services/operating-system",
          label: "Operating System and Governance",
          note: "The weekly leadership rhythm and scorecard that hold a fast-moving Sydney business together.",
        },
        {
          to: "/scan",
          label: "Operating Scan",
          note: "A free self-assessment that takes about six minutes and returns where your constraint most likely sits.",
        },
      ]}
      cta={{
        heading: "Sydney fit calls, usually within the week",
        body: "Sydney is the home base, so on-site time is straightforward and the first session can normally be held in person. The fit call runs thirty minutes and ends with an agreed next step.",
        linkLabel: "Book a fit call",
      }}
      faqs={faqs}
      beforeFaq={
        <section>
          <h2 className="mt-16 font-serif text-3xl text-foreground">How much does a fractional COO cost in Sydney?</h2>
          <p className="mt-6 text-lg leading-relaxed text-charcoal">
            The same as anywhere in Australia with Ajility: there is no metropolitan loading. Market guides put Sydney fractional COO retainers between $8,000 and $16,000 a month plus GST (Expert360's 2026 hiring guide; SEEK salary data 2025 as cited by Fractionus). Ajility's Operating Diagnostic is {answerPrice("Operating Diagnostic")} and composed retainers start {answerPrice("Fortnightly rhythm base")}, delivered on site in Sydney.
          </p>
        </section>
      }
    />
  );
}
